import moment from 'moment';

import { creditCardType } from '@r6digital/rapidstor-types/react/entities/CreditCard';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import {
    AutoBillPayload,
    AutoBillType,
} from '@r6digital/rapidstor-types/react/entities/Reservation';
import {
    BaseReservation,
    RapidStorReservationRequest,
    ReservationPatchRequest,
    ReservationPreMoveInPatchRequest,
} from '@r6digital/rapidstor-types/types/Reservation';

import Logger from '@/utils/logging';
import { RapidStorSettings } from '@/utils/rapidstor';
import { validateString } from '@/utils/validation';

import { ContactData, ReservationData } from '@/store/bookingSlice';

import BaseService from './BaseService';

export default class ReservationService extends BaseService {
    buildPatchPayload = (
        token: string,
        neededDate: string,
        unitType: string,
        deal: string | null,
        cart: Record<string, number>,
        charges: Record<string, number>
    ): ReservationPatchRequest => {
        return {
            locationCode: this._location.locationCode,
            corpCode: this._location.corpCode,
            token: token,
            payload: {
                tenant: {},
                reservation: {
                    neededDate: neededDate,
                    unitType: unitType,
                    deal: deal,
                    enquiry: false,
                },
                cart: cart,
                charges: charges,
            },
        };
    };

    buildAutobillObject = (form: Record<string, string>): AutoBillPayload => {
        let autoBillType = AutoBillType.None;

        if (this._location.rapidstor.useAutoBilling) {
            if (form.autobillType === 'cc') {
                autoBillType = AutoBillType.CreditCard;
            } else if (form.autobillType === 'dd') {
                autoBillType = AutoBillType.DirectDebit;
            } else {
                autoBillType = AutoBillType.None;
            }
        }

        const autoBillObject: AutoBillPayload = {
            usePaymentDetails: form.autobillUsePaymentDetails === 'true' && 
                autoBillType === AutoBillType.CreditCard 
                ? true 
                : false, // Currently only allow card payments for move-in
            type: autoBillType,
            enabled: autoBillType > AutoBillType.None
        };

        // Check if using a payment gateway, if true we don't use autobill
        if (this._location.rapidstor.paymentGateways.use) {
            autoBillObject.type = AutoBillType.None;

            return autoBillObject;
        }

        if (
            this._location.rapidstor.useAutoBilling && 
            this._location.rapidstor.requireAutobill && 
            autoBillObject.type === AutoBillType.None
        ) {
            throw new Error('Autobill is required');
        }

        // Empty data check, if the user hasn't filled out the autobill form details then set the type to None and return
        if (autoBillObject.type === AutoBillType.CreditCard && !autoBillObject.usePaymentDetails) {
            if (
                !validateString(form.autobillCreditCardExpiryMonth) ||
                !validateString(form.autobillCreditCardExpiryYear) ||
                !validateString(form.autobillCreditCardNumber) ||
                !validateString(form.autobillCreditCardCsv) ||
                !validateString(form.autobillCreditCardName)
            ) {
                autoBillObject.type = AutoBillType.None;
                autoBillObject.enabled = false;

                return autoBillObject;
            }
        } else if (autoBillObject.type === AutoBillType.DirectDebit) {
            if (
                !validateString(form.autobillAccountNumber) ||
                !validateString(form.autobillBsbNumber) ||
                !validateString(form.autobillAccountType)
            ) {
                autoBillObject.type = AutoBillType.None;
                autoBillObject.enabled = false;

                return autoBillObject;
            }
        }

        if (autoBillObject.type === AutoBillType.CreditCard) {
            if (form.autobillUsePaymentDetails === 'true') {
                Logger.info('Using existing credit card details', this._location);
                autoBillObject.creditCardExpiryMonth = form.cardExpiryMonth;
                autoBillObject.creditCardExpiryYear = form.cardExpiryYear;
                autoBillObject.creditCardNumber = form.cardNumber;
                autoBillObject.creditCardSecurityCode = form.cardCsv;
                autoBillObject.creditCardName = form.cardHolderName;
            } else {
                Logger.info('Using new credit card details', this._location);
                autoBillObject.creditCardExpiryMonth = form.autobillCreditCardExpiryMonth;
                autoBillObject.creditCardExpiryYear = form.autobillCreditCardExpiryYear;
                autoBillObject.creditCardNumber = form.autobillCreditCardNumber;
                autoBillObject.creditCardSecurityCode = form.autobillCreditCardCsv;
                autoBillObject.creditCardName = form.autobillCreditCardName;
            }

            const creditCardTypeValue = creditCardType(autoBillObject.creditCardNumber);
            if (!creditCardTypeValue) {
                throw new Error('Invalid credit card number');
            }
            autoBillObject.creditCardType = `${creditCardTypeValue}`;
        } else if (autoBillObject.type === AutoBillType.DirectDebit) {
            Logger.info('Using direct debit details', this._location);
            autoBillObject.accountNumber = form.autobillAccountNumber;
            autoBillObject.bsbNumber = form.autobillBsbNumber;
            autoBillObject.accountType = form.autobillAccountType;
        }

        return autoBillObject;
    };

    buildAltContactObject = (
        form: Record<string, string>,
        location: Location
    ) => {
        const showAltContact = RapidStorSettings(location).alternateContact().show();
        return {
            title: showAltContact ? form.title_alternate : '',
            firstName: showAltContact ? form.firstName_alternate : '',
            lastName: showAltContact ? form.lastName_alternate : '',
            email: showAltContact ? form.email_alternate : '',
            city: showAltContact ? form.city_alternate : '',
            phone: showAltContact ? form.phone_alternate : '',
            state: showAltContact ? form.state_alternate : '',
            address: showAltContact ? form.address_alternate : '',
            postalCode: showAltContact ? form.postcode_alternate : '',
        };
    }

    buildPreMoveInPatchPayload = (
        reservation: BaseReservation,
        formData: Record<string, string>,
        cart: Record<string, number>,
        charges: Record<string, number>,
        location: Location
    ): ReservationPreMoveInPatchRequest => {
        const autoBillObject = this.buildAutobillObject(formData);
        const altContactObject = this.buildAltContactObject(formData, location);
        const result: ReservationPreMoveInPatchRequest = {
            locationCode: this._location.locationCode,
            corpCode: this._location.corpCode,
            token: reservation.token,
            payload: {
                autobill: autoBillObject,
                tenant: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    address: formData.address,
                    city: formData.city,
                    state: formData.state,
                    postalCode: formData.postcode,
                    country: formData.country,
                    password: formData.password,
                    company: formData.company,
                    companyRegistrationNum: formData.companyNumber,
                    alternativeContact: altContactObject,
                    commercial: formData.tenant_commercial === 'true' ? true : false,
                    dateOfBirth: formData.dateOfBirth ? moment(formData.dateOfBirth).format("YYYY-MM-DD") : '',
                    idDocNumber: formData.idDocumentNumber,
                    license: formData.driverLicenseNumber,
                    licenseState: formData.driverLicenseState,
                    title: formData.title,
                },
                reservation: {
                    insuranceCoverageId: formData.insuranceCoverage === '' ? false : formData.insuranceCoverage,
                    insuranceType: formData.insuranceCoverage !== '' ? 'site' : 'self',
                    insuranceDeclaredAmt: Number(formData.declaredValueOfGoods),
                    marketingAgree: formData.marketingAgree === 'true' ? true : false,
                    termsAndConditionsAgree:
                        formData.termsAndConditionsAgree === 'true' ? true : false,
                    neededDate: moment(formData.neededDate).format('YYYY-MM-DD'),
                    deal: reservation.deal?._id,
                },
                cart: cart,
                charges: charges,
            },
        };

        if (autoBillObject.type !== AutoBillType.None) {
            result.payload.tenant.autoBill = true;
            result.payload.autobill = { 
                ...autoBillObject,
                enabled: true
            }
        }

        return result;
    };

    buildPostPayload = (
        reservationData: ReservationData,
        enquiry: boolean,
        webhookOnly: boolean,
        contactData: ContactData | null,
        sessionId: string,
        neededDate?: string,
        gclid?: string
    ): RapidStorReservationRequest => {
        return {
            locationCode: this._location.locationCode,
            corpCode: this._location.corpCode,
            payload: {
                customListing: true,
                enquiry: enquiry,
                webhookOnly: webhookOnly,
                neededDate: neededDate,
                timezoneOffset: this._location.timezoneOffset * -60,
                sourceUrl: window.location.href,
                deal: reservationData.deal,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                insuranceCoverageId: reservationData.descriptor.defaultInsuranceCoverage,
                tenant: {
                    tenantId: null,
                    firstName: contactData?.firstName ?? '',
                    lastName: contactData?.lastName ?? '',
                    email: contactData?.email ?? '',
                    phone: contactData?.phone ?? '',
                },
                unitType: reservationData.unitType._id,
                externalData: {
                    SessionID: sessionId,
                },
                googleClickId: gclid,
            },
        };
    };
}
